import { Box, Link, Typography } from '@mui/material';
import { CustomTooltip } from '../keywordResearch/CustomTooltip';
import { keywordResearchDashBoardCss } from '../keywordResearch/keywordResearchCSS';
import { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  updateMapScreenshot,
  setMapScreenshotSignal
} from 'src/redux/localSeo';
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  Circle
} from '@react-google-maps/api';
import { GoogleMapKey } from 'src/appConfig';
import { MapRankCss } from '../mapRankTracker/MapRankCss';
import CommonModal from 'src/common/config/CommonModal';
import { ResultsModal } from './ResultsModal';

// Static array so LoadScript doesn't reload on every render
const GOOGLE_MAP_LIBRARIES: ('places')[] = ['places'];

// ============================================================================
// HELPER FUNCTIONS (Outside component for better stability)
// ============================================================================

const toRadians = (deg: number) => (deg * Math.PI) / 180;

const getDistanceInMeters = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const getZoomLevel = (distanceInMeters: number) => {
  const scale = distanceInMeters / 500;
  const zoomLevel = 16 - Math.log2(scale || 1);
  return Math.min(Math.max(zoomLevel, 1), 21);
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const CustomCircle = ({
  point,
  isExporting,
  isSelected,
  handleCircleClick,
  tooltipContent
}: {
  point: any;
  isExporting: boolean;
  isSelected?: boolean;
  handleCircleClick: (pos: any) => void;
  tooltipContent?: React.ReactNode;
}) => {
  const { latitude, longitude, rank } = point;
  const value = rank;
  const isNotFound = value < 0;
  const isZero = value === 0;

  let backgroundColor = '#FD853A'; // default orange
  let borderColor = '#FB6514';

  if (isNotFound) {
    backgroundColor = '#767676';
    borderColor = '#767676';
  } else if (isZero) {
    backgroundColor = '#F04438';
    borderColor = '#B42318';
  } else if (value <= 3) {
    backgroundColor = '#12B76A'; // green
    borderColor = '#039855';
  } else if (value >= 10) {
    backgroundColor = '#F04438'; // red
    borderColor = '#B42318';
  }

  const text = isNotFound ? 'N/F' : isZero ? '-' : value;
  const circleRef = useRef(null);

  const CircleBox = (
    <Box
      ref={circleRef}
      sx={{
        ...MapRankCss?.mapOverLayBoxSx,
        backgroundColor,
        border: isSelected ? `4px solid ${borderColor}` : 'none',
        borderRadius: '50%',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: isNotFound ? '12px' : '14px',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isExporting ? 'default' : 'pointer'
      }}
      onClick={() => {
        if (!isExporting) {
          handleCircleClick({ lat: latitude, lng: longitude, point });
        }
      }}
    >
      {text}
    </Box>
  );

  return (
    <OverlayView
      position={{ lat: latitude, lng: longitude }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      {isSelected && !isExporting ? (
        <CustomTooltip title={tooltipContent} placement="top" arrow>
          {CircleBox}
        </CustomTooltip>
      ) : (
        CircleBox
      )}
    </OverlayView>
  );
};

const getRankCircleMetadata = (rank: number) => {
  const isNF = rank < 0;
  const isZero = rank === 0;
  let bgColor = '#FD853A'; // orange
  let label = rank.toString();

  if (isNF) {
    bgColor = '#767676';
    label = 'N/F';
  } else if (isZero) {
    bgColor = '#F04438';
    label = '-';
  } else if (rank <= 3) {
    bgColor = '#12B76A'; // green
  } else if (rank >= 10) {
    bgColor = '#F04438'; // red
    label = rank.toString();
  }
  return { bgColor, label, isNF };
};

export const LocalRankingMap = ({
  reportData,
  isExporting = false,
  isPdfBuilder = false
}) => {
  const [selectedLocation, setSelectedLocation]: any = useState(null);
  const [openResultModal, setOpenResultModal] = useState(false);
  const [mapType, setMapType]: any = useState('roadmap');

  // Base64 screenshot captured before PDF export — used as the map image in the PDF
  const mapScreenshots = useSelector(
    (state: RootState) => state.localSeo.mapScreenshots
  );
  const mapScreenshotSignal = useSelector(
    (state: RootState) => state.localSeo.mapScreenshotSignal
  );
  const dispatch = useDispatch();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GoogleMapKey,
    libraries: GOOGLE_MAP_LIBRARIES
  });

  // ── Derived values (must be before hooks that use them) ──────────────────
  const gridPoints = reportData?.grid_points ?? [];

  const center = gridPoints.length
    ? {
        lat:
          gridPoints?.reduce((sum, point) => sum + (point.latitude ?? 0), 0) /
          gridPoints?.length,
        lng:
          gridPoints?.reduce((sum, point) => sum + (point.longitude ?? 0), 0) /
          gridPoints?.length
      }
    : { lat: 0, lng: 0 };

  const staticCenter = `${center.lat},${center.lng}`;

  const maxDistance = gridPoints.length
    ? Math.max(
        ...gridPoints.map((pt) =>
          getDistanceInMeters(center.lat, center.lng, pt.latitude, pt.longitude)
        ),
        500
      )
    : 500;

  const zoom = getZoomLevel(maxDistance) - 0.4;

  const getPixelPosition = (
    ptLat: number,
    ptLng: number,
    w = 800,
    h = 600,
    s = 1
  ) => {
    const WORLD_SIZE = 256;
    const project = (lat: number, lng: number, z: number) => {
      const sinLat = Math.sin((lat * Math.PI) / 180);
      const x = ((lng + 180) / 360) * WORLD_SIZE * Math.pow(2, z);
      const y =
        (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
        WORLD_SIZE *
        Math.pow(2, z);
      return { x, y };
    };

    const centerProj = project(center.lat, center.lng, zoom);
    const pointProj = project(ptLat, ptLng, zoom);

    return {
      left: w / 2 + (pointProj.x - centerProj.x) * s,
      top: h / 2 + (pointProj.y - centerProj.y) * s
    };
  };

  const getPixelRadius = (meters: number) => {
    const latRad = (center.lat * Math.PI) / 180;
    const metersPerPixel =
      (156543.03392 * Math.cos(latRad)) / Math.pow(2, zoom);
    return meters / metersPerPixel;
  };

  const getStaticMapUrl = () => {
    return `https://maps.googleapis.com/maps/api/staticmap?center=${staticCenter}&zoom=${Math.round(
      zoom
    )}&size=800x600&scale=2&maptype=${mapType}&key=${GoogleMapKey}`;
  };

  // ── Canvas-based map capture ─────────────────────────────────────────────
  // Triggered when PDFBuilderContainer sets mapScreenshotSignal = 'capturing'.
  useEffect(() => {
    if (mapScreenshotSignal !== 'capturing' || !isExporting || !isPdfBuilder)
      return;
    if (!gridPoints.length) {
      console.warn('MapCapture: No grid points, aborting');
      return;
    }

    const WIDTH = 1600;
    const HEIGHT = 1200;
    const SCALE = 2;

    const captureMap = async () => {
      try {
        const staticUrl = getStaticMapUrl();

        // Fetch as a blob
        const resp = await fetch(staticUrl);
        if (!resp.ok) {
          throw new Error(`Fetch failed with status: ${resp.status}`);
        }
        const blob = await resp.blob();
        const imgUrl = URL.createObjectURL(blob);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imgUrl;

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Static map failed to load'));
        });

        const canvas = document.createElement('canvas');
        canvas.width = WIDTH;
        canvas.height = HEIGHT;
        const ctx = canvas.getContext('2d')!;

        // Draw map
        ctx.drawImage(img, 0, 0, WIDTH, HEIGHT);
        URL.revokeObjectURL(imgUrl);

        // Draw Service Area Circle (Background)
        const areaRadius = getPixelRadius(maxDistance) * SCALE;
        ctx.beginPath();
        ctx.arc(WIDTH / 2, HEIGHT / 2, areaRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(18, 2, 66, 0.1)';
        ctx.fill();

        // Rank circles
        gridPoints.forEach((point: any) => {
          const { rank, latitude, longitude } = point;
          const pos = getPixelPosition(
            latitude,
            longitude,
            WIDTH,
            HEIGHT,
            SCALE
          );
          const cx = pos.left;
          const cy = pos.top;

          const { bgColor, label, isNF } = getRankCircleMetadata(rank);
          const radius = 12 * SCALE;

          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.fillStyle = bgColor;
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5 * SCALE;
          ctx.stroke();

          ctx.fillStyle = 'white';
          ctx.font = `bold ${
            isNF ? 10 * SCALE : 13 * SCALE
          }px Inter, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, cx, cy);
        });

        const base64 = canvas.toDataURL('image/png');
        const keywordName =
          reportData?.keywords?.[0]?.name ||
          reportData?.keywords?.[0]?.keyword ||
          'default';

        dispatch(updateMapScreenshot({ keyword: keywordName, base64 }));
      } catch (err) {
        console.error('MapCapture: Error:', err);
      }
    };

    captureMap();
  }, [
    mapScreenshotSignal,
    isExporting,
    isPdfBuilder,
    gridPoints,
    staticCenter,
    zoom,
    reportData,
    dispatch
  ]);

  const handleCloseResultModal = () => {
    setOpenResultModal(false);
  };

  const handleCircleClick = (position) => {
    setSelectedLocation(position);
  };

  if (!gridPoints.length) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: '#F9FAFB',
          borderRadius: '12px',
          marginTop: '18px'
        }}
      >
        <Typography color="textSecondary">
          No map ranking data available for this keyword.
        </Typography>
      </Box>
    );
  }

  const options = {
    disableDefaultUI: false,
    streetViewControl: false,
    fullscreenControl: true,
    gestureHandling: isExporting || isPdfBuilder ? 'none' : 'cooperative',
    mapTypeId: mapType
  };

  if (loadError) {
    return (
      <Box sx={{ p: 2, border: '1px solid red', borderRadius: '8px' }}>
        <Typography color="error">
          Error loading Google Maps. Please check your internet connection or
          API key.
        </Typography>
      </Box>
    );
  }

  // Compute areaRadiusPx for both the canvas capture and the JSX fallback view
  const areaRadiusPx = getPixelRadius(maxDistance);

  if (isExporting) {
    return (
      <Box
        className="LocalSeoMapFinal"
        sx={{
          width: '100%',
          height: 'auto',
          borderRadius: '12px',
          overflow: 'hidden',
          marginTop: '24px',
          position: 'relative',
          border: '1px solid #D0D5DD',
          display: 'block !important',
          backgroundColor: '#F9FAFB'
        }}
      >
        {(() => {
          const keywordName =
            reportData?.keywords?.[0]?.name ||
            reportData?.keywords?.[0]?.keyword ||
            'default';
          const screenshot = mapScreenshots[keywordName];
          if (screenshot) {
            return (
              <img
                src={screenshot}
                alt="Map Rank"
                crossOrigin="anonymous"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            );
          }
          return null;
        })()}
        {!mapScreenshots[
          reportData?.keywords?.[0]?.name ||
            reportData?.keywords?.[0]?.keyword ||
            'default'
        ] && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={getStaticMapUrl()}
              alt="Map Rank Static"
              crossOrigin="anonymous"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            {gridPoints.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '800px',
                    height: '600px',
                    flexShrink: 0
                  }}
                >
                  {/* Local Area Highlight Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '300px',
                      left: '400px',
                      width: `${areaRadiusPx * 2}px`,
                      height: `${areaRadiusPx * 2}px`,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(18, 2, 66, 0.1)',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 1
                    }}
                  />
                  {/* Rank Circle Overlays */}
                  {gridPoints.map((point, index) => {
                    const pos = getPixelPosition(
                      point.latitude,
                      point.longitude,
                      800,
                      600,
                      1
                    );
                    const val = point.rank;
                    const { bgColor, label } = getRankCircleMetadata(val);
                    return (
                      <Box
                        key={index}
                        sx={{
                          position: 'absolute',
                          left: `${pos.left}px`,
                          top: `${pos.top}px`,
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: bgColor,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: label === 'N/F' ? '10px' : '14px',
                          fontWeight: 700,
                          transform: 'translate(-50%, -50%)',
                          border: '1px solid white',
                          zIndex: 2
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (!isLoaded) return <Typography sx={{ m: 2 }}>Loading Map...</Typography>;

  return (
    <>
      <Box sx={{ marginTop: '12px' }}>
        <Box
          className="LocalSeoMapLiveContainer"
          sx={{
            width: '100%',
            height: '600px',
            borderRadius: '12px',
            border: '1px solid #D0D5DD',
            overflow: 'hidden',
            pointerEvents: isPdfBuilder ? 'none' : 'auto'
          }}
        >
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={zoom}
            options={options}
            onLoad={(map) => setMapType(map.getMapTypeId())}
          >
            {gridPoints.length > 0 && (
              <Circle
                center={center}
                radius={maxDistance}
                options={{
                  fillColor:
                    mapType === 'hybrid'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : 'rgba(18, 2, 66, 0.1)',
                  fillOpacity: 1,
                  strokeColor: 'transparent',
                  strokeWeight: 0
                }}
              />
            )}

            {gridPoints.map((point: any, idx) => {
              const isSelected =
                selectedLocation?.lat === point.latitude &&
                selectedLocation?.lng === point.longitude;
              const tooltipTitles =
                point?.higher_ranked_competitors
                  ?.slice(0, 3)
                  ?.map((c) => c.title) ?? [];
              const tooltipContent = (
                <Box>
                  {tooltipTitles.map((title, index) => (
                    <Typography
                      key={index}
                      sx={{
                        marginBottom: '4px',
                        ...keywordResearchDashBoardCss?.customTollTipText
                      }}
                    >
                      {`${index + 1}. `}
                      {title}
                    </Typography>
                  ))}
                  <Box mt={1}>
                    <Link
                      href="#"
                      underline="always"
                      sx={{ fontWeight: 500, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenResultModal(true);
                      }}
                    >
                      Click here to learn more
                    </Link>
                  </Box>
                </Box>
              );

              return (
                <CustomCircle
                  key={idx}
                  point={point}
                  isExporting={false}
                  isSelected={isSelected}
                  handleCircleClick={handleCircleClick}
                  tooltipContent={tooltipContent}
                />
              );
            })}
          </GoogleMap>
        </Box>
      </Box>

      <CommonModal
        open={openResultModal}
        handCloseModal={handleCloseResultModal}
        title={''}
        closebtn={false}
        isAgencyModal={true}
        isInfoModal={true}
        isFreeDowngrade={true}
        isDynamicWidth={'727px'}
      >
        <ResultsModal
          handleClose={handleCloseResultModal}
          selectedData={selectedLocation?.point}
          reportData={reportData}
        />
      </CommonModal>
    </>
  );
};
